import { t } from '../../../../../common/i18n.js'
import { Language } from '../../../../../common/types/i18n.js'
import { Lock } from '../../../../components/icon/solar-icons.js'
import { PageHeader } from '../../../../components/page-header/page-header.js'
import { PageShell } from '../../../../components/page-shell/page-shell.js'
import { Spinner } from '../../../../components/spinner/spinner.js'
import { SubNav, SubNavItem } from '../../../../components/sub-nav/sub-nav.js'
import type {
    LeavePolicyBundle,
    PortalPermissions,
    SettingsPolicy,
} from '../../utils/portal-api.js'
import { AuditLogView } from '../audit-log/audit-log-view.js'
import { ErrorLogView } from '../error-log/error-log-view.js'
import { CompensationTypesSection } from './sections/compensation-types-section.js'
import { CustomFieldDef, CustomFieldsSection } from './sections/custom-fields-section.js'
import { GeneralSection } from './sections/general-section.js'
import { Holiday, HolidaysSection } from './sections/holidays-section.js'
import { LeavePolicyBundlesSection } from './sections/leave-policy-bundles-section.js'
import { LeaveTypesSection } from './sections/leave-types-section.js'
import { LocationsSection } from './sections/locations-section.js'
import type { PickerEmployee } from './sections/policy-employee-picker.js'
import { RolesSection } from './sections/roles-section.js'
import { TeamsSection } from './sections/teams-section.js'
import { ViewAsSection } from './sections/view-as-section.js'
import { WorkingPatternsSection } from './sections/working-patterns-section.js'
import { allowedSections } from './settings-section.js'
import './settings-view.css'

export type SettingsTab =
    | 'general'
    | 'leavePolicies'
    | 'leaveTypes'
    | 'holidays'
    | 'workingPatterns'
    | 'teams'
    | 'locations'
    | 'customFields'
    | 'compensationTypes'
    | 'roles'
    | 'viewAs'
    | 'auditLog'
    | 'errorLog'

interface Company {
    id: number
    name: string
    reg_code: string | null
    standard_day_hours: number
}

export interface SettingsViewProps {
    lang: Language
    permissions: PortalPermissions
    activeTab: SettingsTab
    loading: boolean

    companies: Company[]

    // Bundle editor's type picker lists these; per-type config editing moved to
    // the "Eemaloleku tüübid" tab (self-contained LeaveTypesSection).
    policies: SettingsPolicy[]

    bundles: LeavePolicyBundle[]
    bundleSlideOpen: boolean
    editingBundle: LeavePolicyBundle | null
    bundleForm: { name: string; policyIds: number[] }
    bundleSaving: boolean
    bundleError: string | null
    onOpenBundleCreate: () => void
    onOpenBundleEdit: (bundle: LeavePolicyBundle) => void
    onCloseBundleSlide: () => void
    onUpdateBundleName: (value: string) => void
    onToggleBundlePolicy: (policyId: number) => void
    onSaveBundle: () => void
    onDeleteBundle: (id: number) => void
    bundleEmployeeIds: number[]
    bundleSelectedEmployees: PickerEmployee[]
    onAddBundleEmployee: (emp: PickerEmployee) => void
    onRemoveBundleEmployee: (id: number) => void
    onSelectAllBundleEmployees: (employees: PickerEmployee[]) => void
    onClearAllBundleEmployees: () => void

    holidays: Holiday[]
    holidayYear: number
    onChangeHolidayYear: (year: number) => void
    holidayCompanyId: number
    onChangeHolidayCompany: (companyId: number) => void
    onAddHoliday: (date: string, name: string, companyId: number) => void
    onUpdateHoliday: (id: number, date: string, name: string) => void
    onDeleteHoliday: (id: number) => void

    customFields: CustomFieldDef[]
    fieldSlideOpen: boolean
    editingField: CustomFieldDef | null
    fieldForm: { name: string; field_type: string; options: string; visibility: string }
    savingField: boolean
    onOpenFieldCreate: () => void
    onOpenFieldEdit: (field: CustomFieldDef) => void
    onCloseFieldSlide: () => void
    onUpdateFieldForm: (field: string, value: string) => void
    onSaveField: () => void
    onDeleteField: (id: number) => void

    showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
}

// The tab's label. Audit log reuses the nav label; the rest map onto the
// settings tab translations by their matching key.
const tabLabel = (tab: SettingsTab, lang: Language): string =>
    tab === 'auditLog'
        ? t.portal.nav.auditLog(lang)
        : tab === 'errorLog'
          ? t.portal.nav.errorLog(lang)
          : t.portal.settings.tabs[tab](lang)

export const SettingsView = (props: SettingsViewProps) => {
    const { lang, permissions, activeTab } = props

    // The tabs the held admin permission keys allow, in tab-row order.
    const sections = allowedSections(permissions)

    if (sections.length === 0) {
        return (
            <PageShell>
                <div className="portal-settings__empty">
                    <p>
                        <Lock size={24} />
                    </p>
                    <p>{t.portal.settings.description(lang)}</p>
                </div>
            </PageShell>
        )
    }

    const navItems: SubNavItem[] = sections.map((section) => ({
        key: section.tab,
        label: tabLabel(section.tab, lang),
        href: '/settings/' + section.segment,
    }))

    // The route guarantees activeTab is permitted; fall back defensively.
    const effectiveTab: SettingsTab = sections.some((s) => s.tab === activeTab)
        ? activeTab
        : sections[0].tab

    // Tabs whose data is fetched by SettingsViewWired; the rest fetch their own
    // and render their own loading state. While the wired data loads, show the
    // spinner in the content area, keeping the page header + tab strip stable.
    const wiredTabs: SettingsTab[] = ['general', 'leavePolicies', 'holidays', 'customFields']
    const showWiredSpinner = props.loading && wiredTabs.includes(effectiveTab)

    return (
        <PageShell>
            <PageHeader
                title={t.portal.settings.settingsTitle(lang)}
                subtitle={t.portal.settings.settingsDescription(lang)}
            />

            <SubNav items={navItems} activeKey={effectiveTab} lang={lang} />

            {/* Content */}
            <div className="portal-settings__content">
                {showWiredSpinner && <Spinner />}

                {!showWiredSpinner && effectiveTab === 'general' && (
                    <GeneralSection lang={lang} companies={props.companies} />
                )}

                {!showWiredSpinner && effectiveTab === 'leavePolicies' && (
                    <LeavePolicyBundlesSection
                        lang={lang}
                        bundles={props.bundles}
                        policies={props.policies}
                        slideOpen={props.bundleSlideOpen}
                        editingBundle={props.editingBundle}
                        bundleForm={props.bundleForm}
                        saving={props.bundleSaving}
                        error={props.bundleError}
                        onOpenCreate={props.onOpenBundleCreate}
                        onOpenEdit={props.onOpenBundleEdit}
                        onClose={props.onCloseBundleSlide}
                        onUpdateName={props.onUpdateBundleName}
                        onTogglePolicy={props.onToggleBundlePolicy}
                        onSave={props.onSaveBundle}
                        onDelete={props.onDeleteBundle}
                        employeeIds={props.bundleEmployeeIds}
                        selectedEmployees={props.bundleSelectedEmployees}
                        onAddEmployee={props.onAddBundleEmployee}
                        onRemoveEmployee={props.onRemoveBundleEmployee}
                        onSelectAllEmployees={props.onSelectAllBundleEmployees}
                        onClearAllEmployees={props.onClearAllBundleEmployees}
                    />
                )}

                {!showWiredSpinner && effectiveTab === 'holidays' && (
                    <HolidaysSection
                        lang={lang}
                        holidays={props.holidays}
                        year={props.holidayYear}
                        companies={props.companies}
                        selectedCompanyId={props.holidayCompanyId}
                        onChangeYear={props.onChangeHolidayYear}
                        onChangeCompany={props.onChangeHolidayCompany}
                        onAdd={props.onAddHoliday}
                        onUpdate={props.onUpdateHoliday}
                        onDelete={props.onDeleteHoliday}
                    />
                )}

                {!showWiredSpinner && effectiveTab === 'customFields' && (
                    <CustomFieldsSection
                        lang={lang}
                        fields={props.customFields}
                        slideOpen={props.fieldSlideOpen}
                        editingField={props.editingField}
                        fieldForm={props.fieldForm}
                        saving={props.savingField}
                        onOpenCreate={props.onOpenFieldCreate}
                        onOpenEdit={props.onOpenFieldEdit}
                        onClose={props.onCloseFieldSlide}
                        onUpdateForm={props.onUpdateFieldForm}
                        onSave={props.onSaveField}
                        onDelete={props.onDeleteField}
                    />
                )}

                {effectiveTab === 'roles' && (
                    <RolesSection lang={lang} showToast={props.showToast} />
                )}

                {effectiveTab === 'locations' && (
                    <LocationsSection lang={lang} showToast={props.showToast} />
                )}

                {effectiveTab === 'teams' && (
                    <TeamsSection
                        lang={lang}
                        showToast={props.showToast}
                        companies={props.companies}
                    />
                )}

                {effectiveTab === 'leaveTypes' && (
                    <LeaveTypesSection lang={lang} showToast={props.showToast} />
                )}

                {effectiveTab === 'workingPatterns' && (
                    <WorkingPatternsSection lang={lang} showToast={props.showToast} />
                )}

                {effectiveTab === 'compensationTypes' && (
                    <CompensationTypesSection lang={lang} showToast={props.showToast} />
                )}

                {effectiveTab === 'viewAs' && (
                    <ViewAsSection lang={lang} showToast={props.showToast} />
                )}

                {effectiveTab === 'auditLog' && <AuditLogView lang={lang} embedded />}

                {effectiveTab === 'errorLog' && <ErrorLogView lang={lang} embedded />}
            </div>
        </PageShell>
    )
}
